import { SingleSelectFilter } from "@components/job_search/filters/single_select_filter.tsx";
import { MultiSelectFilter } from "@components/job_search/filters/multi_select_filter.tsx";
import { TertiaryButton } from "@sprout/buttons";
import useTranslation from "@hooks/use_translation.ts";
import {
  useDatePostedOptions,
  useEmploymentTypeOptions,
  useSalaryOptions,
  useWorkTypeOptions,
} from "@hooks/job_search/use_filter_options.ts";
import { useMemo } from "react";
import { cn } from "@/lib/utils";

export type Props = {
  datePosted: string;
  onDatePostedChange: (query: string) => void;
  salary: string;
  onSalaryChange: (query: string) => void;
  workType: string[];
  onWorkTypeChange: (query: string[]) => void;
  employmentType: string[];
  onEmploymentTypeChange: (query: string[]) => void;
  fullBleed?: boolean;
};

export const JobSearchFilters = ({
  datePosted,
  onDatePostedChange,
  salary,
  onSalaryChange,
  workType,
  onWorkTypeChange,
  employmentType,
  onEmploymentTypeChange,
  fullBleed = false,
}: Props) => {
  const { t } = useTranslation("jobSearch");

  const datePostedOptions = useDatePostedOptions();
  const salaryOptions = useSalaryOptions();
  const workTypeOptions = useWorkTypeOptions();
  const employmentTypeOptions = useEmploymentTypeOptions();

  const resetFilters = () => {
    onDatePostedChange("");
    onSalaryChange("");
    onWorkTypeChange([]);
    onEmploymentTypeChange([]);
  };

  const hasActiveFilters = useMemo(
    () => datePosted !== "" || salary !== "" || workType.length > 0 || employmentType.length > 0,
    [datePosted, salary, workType, employmentType],
  );

  return (
    <div
      className={cn(
        "pb-2 overflow-x-auto hide-mobile-scrollbar",
        fullBleed && "-ml-7 -mr-6 pl-7 pr-6",
      )}
    >
      <div className={cn("flex gap-3 w-max text-nowrap items-center", fullBleed && "mx-auto")}>
        <SingleSelectFilter
          name={t("datePosted")}
          options={datePostedOptions}
          value={datePosted}
          onValueChange={onDatePostedChange}
        />
        <SingleSelectFilter
          name={t("salary")}
          options={salaryOptions}
          value={salary}
          onValueChange={onSalaryChange}
        />
        <MultiSelectFilter
          name={t("workType")}
          options={workTypeOptions}
          values={workType}
          onValuesChange={onWorkTypeChange}
        />
        <MultiSelectFilter
          name={t("employmentType")}
          options={employmentTypeOptions}
          values={employmentType}
          onValuesChange={onEmploymentTypeChange}
        />
        {hasActiveFilters && (
          <TertiaryButton disabled={!hasActiveFilters} secondary onClick={resetFilters}>
            {t("resetFilters")}
          </TertiaryButton>
        )}
      </div>
    </div>
  );
};
